import styled from "@emotion/styled";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EKIDEN_API_URL } from "@constants";
import { useMedia } from "@hooks/useMedia";
import { type PingConnectionStatus, usePing } from "@hooks/usePing";
import { media } from "@themes/breakpoints";
import { theme } from "@themes/themes";

/** Slightly faster than the default status page interval so the footer feels current. */
const FOOTER_LATENCY_INTERVAL_MS = 15_000;

const BAR_HEIGHTS = [4, 6, 9, 12] as const;

const statusToColor = (status: PingConnectionStatus) => {
  switch (status) {
    case "success":
      return theme.color("green-strong");
    case "moderate":
      return theme.color("accent-11");
    case "slow":
      return theme.color("yellow-strong");
    case "error":
      return theme.color("red-strong");
    default:
      return theme.color("gray-9");
  }
};

const activeBarCount = (status: PingConnectionStatus): number => {
  switch (status) {
    case "success":
      return 4;
    case "moderate":
      return 3;
    case "slow":
      return 2;
    case "error":
      return 1;
    default:
      return 0;
  }
};

export const FooterConnectionLatency = () => {
  const { t } = useTranslation();
  const { mobile } = useMedia();
  const urls = useMemo(() => (EKIDEN_API_URL ? [EKIDEN_API_URL] : []), []);
  const { results } = usePing(urls, { intervalMs: FOOTER_LATENCY_INTERVAL_MS });
  const result = results[0];

  if (!EKIDEN_API_URL || !result) {
    return null;
  }

  const { responseTime, status } = result;
  const color = statusToColor(status);
  const filled = activeBarCount(status);

  const statusLabel = ((): string => {
    switch (status) {
      case "success":
        return String(t("ping.stableConnection"));
      case "moderate":
        return String(t("ping.fairConnection"));
      case "slow":
        return String(t("ping.slowConnection"));
      case "error":
        return String(t("common.offline"));
      case "loading":
        return String(t("common.checking"));
      default:
        return String(t("common.unknown"));
    }
  })();

  const latencyPart =
    status === "loading" || status === "error"
      ? ""
      : responseTime !== null
        ? ` (${Math.round(responseTime)}ms)`
        : ` (${String(t("ping.na"))})`;

  const latencyMsOnly = responseTime !== null ? `${Math.round(responseTime)}ms` : String(t("ping.na"));

  const useCompactMs =
    mobile && (status === "success" || status === "moderate" || status === "slow") && responseTime !== null;

  return (
    <Root aria-live="polite" $color={color} title={String(EKIDEN_API_URL)}>
      <SignalBars aria-hidden $status={status}>
        {BAR_HEIGHTS.map((h, i) => (
          <Bar key={h} $active={status === "loading" || i < filled} $height={h} $status={status} />
        ))}
      </SignalBars>
      {useCompactMs ? (
        <span>{latencyMsOnly}</span>
      ) : (
        <span>
          {statusLabel}
          {latencyPart}
        </span>
      )}
    </Root>
  );
};

const Root = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-shrink: 1;
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  user-select: none;

  ${media.mobile} {
    gap: 6px;
    font-size: 11px;
  }
`;

const SignalBars = styled.div<{ $status: PingConnectionStatus }>`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 12px;

  ${({ $status }) =>
    $status === "loading" &&
    `
    @keyframes footer-latency-pulse {
      50% { opacity: 0.45; }
    }
    animation: footer-latency-pulse 1.2s ease-in-out infinite;
  `}
`;

const Bar = styled.div<{ $active: boolean; $height: number; $status: PingConnectionStatus }>`
  width: 3px;
  height: ${({ $height }) => $height}px;
  border-radius: 1px;
  background-color: ${({ $active, $status }) => {
    if ($status === "loading") return theme.color("gray-7");
    return $active ? "currentColor" : theme.color("gray-6");
  }};
  opacity: ${({ $active, $status }) => ($status === "loading" ? 1 : $active ? 1 : 0.35)};
`;
